import { z } from 'zod';

import { AgentIdSchema, AgentKindSchema } from '../agent/agent';
import { CardIdSchema } from '../card/card_id';
import { PrioritySchema } from '../card/priority';

import { BlockReasonSchema } from './block_reason';
import { TaskIdSchema } from './task_id';

export { TaskIdSchema, type TaskId } from './task_id';

/**
 * Lifecycle states a task moves through. Spec v4 §Task lifecycle:
 *
 *   pending → running → done
 *   pending → blocked → running
 *   running → blocked
 *   running → failed
 *   blocked → failed
 *
 * `done` and `failed` are terminal.
 */
export const TaskStatusSchema = z.enum(['pending', 'running', 'blocked', 'done', 'failed']);

export type TaskStatus = z.infer<typeof TaskStatusSchema>;

/**
 * Who drives the task. Spec v4 §Actor — `kind` is the discriminator
 * and `id` is a {@link AgentIdSchema} regardless of branch (the kind
 * comes off the underlying `Agent` document, not the actor envelope).
 */
export const ActorSchema = z.object({
  kind: AgentKindSchema,
  id: AgentIdSchema,
});

export type Actor = z.infer<typeof ActorSchema>;

/**
 * Plain-object task shape. The invariants
 *   - `status === 'blocked'` ⇔ `blockReason !== null`
 *   - `status === 'failed'`  ⇔ `errorCode !== null`
 * are enforced via {@link applyTaskStatusRefinement}; reuse via
 * `.extend(...)` and re-apply the refinement when deriving (e.g. for
 * the server-side `TaskDocumentSchema`).
 *
 * `assigneeAgent` identifies who is responsible for the task. Any kind
 * (`agent` | `operator`) is allowed; `null` means the task is queued without
 * an owner yet (e.g. awaiting operator pickup after AI handoff).
 */
export const TaskSchemaBase = z.object({
  id: TaskIdSchema,
  cardId: CardIdSchema,
  description: z.string().min(1).max(2000),
  assigneeAgent: ActorSchema.nullable(),
  status: TaskStatusSchema,
  blockReason: BlockReasonSchema.nullable(),
  errorCode: z.string().min(1).max(100).nullable(),
  /**
   * Denormalized from `Card.priority` so the task board can sort
   * `(priority DESC, createdAt DESC)` using a compound index without
   * a `$lookup`. Kept in sync at write time (Phase 3 use cases).
   */
  priority: PrioritySchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Adds the cross-field refinements that wire `status` to `blockReason`
 * and `errorCode`. Spec v4 invariants — also enforced server-side at
 * the mutation use cases.
 */
export function applyTaskStatusRefinement<T extends typeof TaskSchemaBase>(schema: T) {
  return schema.superRefine((task, ctx) => {
    if (task.status === 'blocked' && task.blockReason === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['blockReason'],
        message: 'blockReason is required when status=blocked',
      });
    }
    if (task.status !== 'blocked' && task.blockReason !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['blockReason'],
        message: 'blockReason must be null when status!=blocked',
      });
    }
    if (task.status === 'failed' && task.errorCode === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['errorCode'],
        message: 'errorCode is required when status=failed',
      });
    }
    if (task.status !== 'failed' && task.errorCode !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['errorCode'],
        message: 'errorCode must be null when status!=failed',
      });
    }
  });
}

export const TaskSchema = applyTaskStatusRefinement(TaskSchemaBase);

export type Task = z.infer<typeof TaskSchema>;
